import { useEffect } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCompanyDetails } from "../../Actions/CompanyAction";
import { globalStyle, getLogoUrl } from "../../global/Utils";
import { landlineForDisplay } from "../../global/landline";
import { CircularProgress } from "@mui/material";
import moment from "moment";
import Swal from "sweetalert2";

const ViewCompany = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("id");
  const { companyDetailsList, isGettingCompanyDetails } = useSelector(
    (state) => state.CompanyListReducer,
  );
  const { userDetails } = useSelector((state) => state?.HeaderReducer);
  const handleDangerVisibleForCompanyDetails = () => {
    Swal.fire({
      title: "Error",
      text: "Company Not Found",
      icon: "error",
      timer: 30000,
    }).then((result) => {
      if (result.isConfirmed) {
        navigate("/main/view-company-list");
      }
    });
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        getCompanyDetails(companyId, handleDangerVisibleForCompanyDetails),
      );
    });
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [dispatch, companyId]);
  const { assignedScenarios, verifiedScenarios } = companyDetailsList;
  if (!companyId) {
    return Navigate({ to: "/main/view-company-list" });
  }
  return (
    <>
      {isGettingCompanyDetails ? (
        <div className="relative min-h-screen">
          <div className="absolute inset-0 flex items-center justify-center">
            <CircularProgress size={50} color="success" />
          </div>
        </div>
      ) : (
        <div
          className="p-6 animate-fade-in"
          style={{ fontFamily: globalStyle.fontFamily }}
        >
          <button
            onClick={() => navigate("/main/view-company-list")}
            className="mb-4 px-4 py-2 rounded text-white"
            style={{
              background: globalStyle.buttonColor,
              fontFamily: globalStyle.fontFamilyButton,
            }}
          >
            ← Back
          </button>
          <h2
            className="text-2xl font-bold mb-4 text-center"
            style={{
              color: globalStyle.blueColor,
              fontFamily: globalStyle.fontFamilyHeading,
            }}
          >
            Company Profile
          </h2>
          {companyDetailsList?.logo && (
            <div className="flex justify-center mb-4">
              <img
                src={getLogoUrl(companyDetailsList.logo)}
                alt={`${companyDetailsList?.companyName || "Company"} Logo`}
                className="h-24 w-24 object-contain rounded-lg border p-2 shadow"
                style={{
                  background: globalStyle.bgColorWhite,
                  borderColor: globalStyle.curveLightColor,
                }}
              />
            </div>
          )}
          <div
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {[
              { label: "Company Name", value: companyDetailsList?.companyName },
              { label: "CEO Name", value: companyDetailsList?.CEOName },
              {
                label: "CEO Contact",
                value: companyDetailsList?.CEOContact
                  ? `+${companyDetailsList?.CEOContact}`
                  : "-",
              },
              {
                label: "Company Landline",
                value: landlineForDisplay(companyDetailsList?.CEOLandline) || "-",
              },
              { label: "CEO Email", value: companyDetailsList?.CEOEmail },
              { label: "CNIC / NTN", value: companyDetailsList?.ntn },
              {
                label: "Sale Tax No. (STRN)",
                value: companyDetailsList?.salesTaxRegNo,
              },
              { label: "POS ID", value: companyDetailsList?.posId },
              {
                label: "Register GSection 236H %",
                value: `${companyDetailsList?.GSection236HRegister ?? 0}%`,
              },
              {
                label: "Non-Register GSection 236H %",
                value: `${companyDetailsList?.GSection236HNonRegister ?? 0}%`,
              },
              {
                label: "Registration Type",
                value: companyDetailsList?.registrationType,
              },
              { label: "Province", value: companyDetailsList?.province },
              {
                label: "Invoice Pattern",
                value: companyDetailsList?.invoicePattern,
              },
              {
                label: "Concat serial number with Product Name",
                value: companyDetailsList?.concatSerialWithProductName || "No",
              },
              {
                label: "Further Tax Applicable",
                value: companyDetailsList?.furtherTaxApplicable || "No",
              },
              {
                label: "Created At",
                value: moment(companyDetailsList?.createdAt).format("LLL"),
              },

              // ✅ Tokens Logic
              userDetails?.role === "admin" &&
                companyDetailsList?.companyStatus === "Sandbox" && {
                  label: "Sandbox Token",
                  value: companyDetailsList?.sandBoxToken,
                },

              userDetails?.role === "admin" &&
                companyDetailsList?.companyStatus === "Production" && {
                  label: "Sandbox Token",
                  value: companyDetailsList?.sandBoxToken,
                },

              userDetails?.role === "admin" &&
                companyDetailsList?.companyStatus === "Production" && {
                  label: "Production Token",
                  value: companyDetailsList?.productiontoken,
                },

              // ✅ Status Chip
              {
                label: "Company Status",
                value: companyDetailsList?.companyStatus,
                isChip: true,
              },
            ]
              .filter(Boolean)
              .map((item, index) => (
                <div key={index}>
                  <p className="font-semibold text-gray-600">{item.label}:</p>

                  {item.isChip ? (
                    <span
                      className={`inline-block px-3 py-1 mt-1 text-sm font-medium rounded-full
              ${
                item.value === "Sandbox"
                  ? "bg-orange-500 text-white"
                  : item.value === "Production"
                    ? "bg-green-500 text-white"
                    : "bg-gray-300 text-gray-700"
              }`}
                    >
                      {item.value}
                    </span>
                  ) : (
                    <p
                      className="text-base break-all"
                      style={{ color: globalStyle.blueColor }}
                    >
                      {item.value || "-"}
                    </p>
                  )}
                </div>
              ))}
          </div>

          {/* Assigned and Verified Scenerios History */}
          <h3
            className="text-xl font-bold mt-8 mb-2"
            style={{ color: globalStyle.curveDarkColor }}
          >
            Assigned and Verified Scenerios
          </h3>
          <div>
            {assignedScenarios?.length > 0 || verifiedScenarios?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Assigned Scenarios */}
                <div
                                  >
                  <h3 className="text-md font-semibold mb-3 text-gray-700">
                    Assigned Scenarios:
                  </h3>

                  {assignedScenarios?.length > 0 ? (
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {assignedScenarios
                        ?.slice()
                        .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
                        .map((item, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs rounded-md border border-[#F97316] bg-[#F97316] text-white"
                          >
                            {item.scenarioCode}
                          </span>
                        ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">
                      No Assigned Scenarios
                    </p>
                  )}
                </div>

                {/* Verified Scenarios */}
                <div
                                  >
                  <h3 className="text-md font-semibold mb-3 text-gray-700">
                    Verified Scenarios:
                  </h3>

                  {verifiedScenarios?.length > 0 ? (
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {verifiedScenarios
                        ?.slice()
                        .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
                        .map((item, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs rounded-md border border-[#10B981] bg-[#10B981] text-white"
                          >
                            {item.scenarioCode}
                          </span>
                        ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">
                      No Verified Scenarios
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500">No Scenarios available.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ViewCompany;
